"use client";

import { c as _c } from "react/compiler-runtime";
import { Search } from "lucide-react";
import { type ChangeEvent, type FormEvent, useCallback, useId, useState } from "react";
import { Button } from "@defuse/ui/button";
import { Input } from "@defuse/ui/input";
import { Label } from "@defuse/ui/label";
import { cn } from "@/lib/utils";
const LABEL = "Filter by wallet address, deposit address or tx hash";
interface SearchFilterProps {
  search: string | undefined;
  depositMemo: string | undefined;
  updateFilters: (filters: {
    search: string | undefined;
    depositMemo: string | undefined;
  }) => void;
}
export function SearchFilter(t0) {
  const $ = _c(30);
  const {
    search,
    depositMemo,
    updateFilters
  } = t0;
  const id = useId();
  const memoId = useId();
  const [inputValue, setInputValue] = useState(search ?? "");
  const [memoValue, setMemoValue] = useState(depositMemo ?? "");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = t2 => {
      const {
        target: t3
      } = t2;
      const {
        value
      } = t3;
      setInputValue(value);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const handleChange = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = t3 => {
      const {
        target: t4
      } = t3;
      const {
        value: value_0
      } = t4;
      setMemoValue(value_0);
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleMemoChange = t2;
  let t3;
  if ($[2] !== inputValue || $[3] !== memoValue || $[4] !== updateFilters) {
    t3 = e => {
      e.preventDefault();
      const searchVal = inputValue === "" ? undefined : inputValue;
      updateFilters({
        search: searchVal,
        depositMemo: searchVal !== undefined && memoValue !== "" ? memoValue : undefined
      });
    };
    $[2] = inputValue;
    $[3] = memoValue;
    $[4] = updateFilters;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const handleSubmit = t3;
  let t4;
  if ($[6] !== id) {
    t4 = <Label className="sr-only" htmlFor={id}>{LABEL}</Label>;
    $[6] = id;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== id || $[9] !== inputValue) {
    t5 = <Input id={id} placeholder={LABEL} value={inputValue} onChange={handleChange} />;
    $[8] = id;
    $[9] = inputValue;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t4 || $[12] !== t5) {
    t6 = <div className="relative flex-1">{t4}{t5}</div>;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== inputValue || $[15] !== memoId || $[16] !== memoValue) {
    t7 = inputValue !== "" && <MemoInput id={`${memoId}-desktop`} value={memoValue} onChange={handleMemoChange} className="hidden shrink-0 md:block md:w-52" />;
    $[14] = inputValue;
    $[15] = memoId;
    $[16] = memoValue;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Button type="submit" variant="outline"><Search className="h-4 w-4" /><span className="sr-only">Search</span></Button>;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t6 || $[20] !== t7) {
    t9 = <div className="flex gap-2">{t6}{t7}{t8}</div>;
    $[19] = t6;
    $[20] = t7;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== inputValue || $[23] !== memoId || $[24] !== memoValue) {
    t10 = inputValue !== "" && <MemoInput id={memoId} value={memoValue} onChange={handleMemoChange} className="md:hidden" inputClassName="h-8 text-xs" />;
    $[22] = inputValue;
    $[23] = memoId;
    $[24] = memoValue;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== handleSubmit || $[27] !== t10 || $[28] !== t9) {
    t11 = <form onSubmit={handleSubmit} className="flex w-full flex-col gap-2 md:max-w-2xl md:self-end">{t9}{t10}</form>;
    $[26] = handleSubmit;
    $[27] = t10;
    $[28] = t9;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  return t11;
}
function MemoInput(t0) {
  const $ = _c(13);
  const {
    id,
    value,
    onChange,
    className,
    inputClassName
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("animate-in fade-in duration-150", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== id) {
    t2 = <Label className="sr-only" htmlFor={id}>Deposit memo (optional)</Label>;
    $[2] = id;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== id || $[5] !== inputClassName || $[6] !== onChange || $[7] !== value) {
    t3 = <Input id={id} placeholder="Deposit memo (optional)" value={value} onChange={onChange} className={inputClassName} />;
    $[4] = id;
    $[5] = inputClassName;
    $[6] = onChange;
    $[7] = value;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t1 || $[10] !== t2 || $[11] !== t3) {
    t4 = <div className={t1}>{t2}{t3}</div>;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}